import React from 'react';

import trim from 'lodash/trim';

import { useClassnames } from '@lumapps/classnames';
import { SelectTextField } from '@lumapps/combobox/components/SelectTextField';
import { useDataAttributes } from '@lumapps/data-attributes';
import { mdiPound } from '@lumapps/lumx/icons';
import { AutocompleteMultipleProps, Chip, Icon, Size, Theme } from '@lumapps/lumx/react';
import { LabelWithHighlightedQuery } from '@lumapps/search/components/LabelWithHighlightedQuery';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { useDebounceValue } from '@lumapps/utils/hooks/useDebounceValue';
import { ALL_SPACES_PATTERN } from '@lumapps/utils/string/constants';
import { BaseLoadingStatus } from '@lumapps/utils/types/BaseLoadingStatus';

import { TAGZ_CONTEXT_NAME } from '../../constants';
import { useSuggestTagzQuery } from '../../hooks/useSuggestTagzQuery';
import { FOLKSONOMY } from '../../keys';
import * as types from '../../types';
import {
    isTagNameValid,
    isTagDuplicated,
    createTagSuggestion,
    formatTagNameInput,
    normalizeTagLabel,
    isTagzSearchValid,
} from '../utils';

import './index.scss';

/** Base classname for the component. */
const CLASSNAME = 'tagz-autocomplete';

export interface TagsAutocompleteProps {
    /** True if the suggestions are on inline mode, meaning that they will not show the form label and the underline decoration. */
    isInline?: boolean;
    /**
     * Whether the field should be automatically focused.
     * Will trigger a focus if changed from false to true.
     *
     * Default = false */
    shouldFocus?: boolean;
    /** List of selected tags. */
    tags?: types.Tag[];
    // /** List of suggested tags. */
    // tagSuggestions: types.Tag[];
    /** Current UI theme */
    theme?: Theme;
    /** Callback executed when the list of selected tags changes. */
    onTagsChange: (tags: types.Tag[]) => void;
    /** Callback executed when the user focuses in the autocomplete. */
    onFocus?(): void;
    /** Callback executed when the user blurs out the autocomplete. */
    onBlur?(): void;
    /** Callback executed when the user focuses a selected tag. */
    onTagFocus?(): void;
    /** Callback executed when the user blurs out a selected tag. */
    onTagBlur?(): void;
    /** whether the label displayed above the autocomplete should be visible or not. */
    shouldHideLabel?: boolean;
    /** icon to be displayed on the autocomplete field */
    icon?: AutocompleteMultipleProps['icon'];
    /**
     * Give the ability to create or not a new tag
     * @default true
     */
    isNewTagAllowed?: boolean;
}

/**
 * Display tags autocomplete.
 *
 * @family Pickers
 */
const TagsAutocomplete: React.FC<TagsAutocompleteProps> = ({
    isInline = false,
    shouldFocus = false,
    tags = [],
    theme,
    onTagsChange,
    onBlur,
    onFocus,
    onTagFocus,
    onTagBlur,
    shouldHideLabel = false,
    icon,
    isNewTagAllowed = true,
}) => {
    const { block } = useClassnames(CLASSNAME);
    const { translateKey, translateAndReplace } = useTranslate();
    const { get } = useDataAttributes(TAGZ_CONTEXT_NAME);

    const [tagName, setTagName] = React.useState<string>('');
    // Debounce the search query to avoid triggering to much fetches
    const debouncedTagName = useDebounceValue(tagName);

    /**
     * Trim the spaces around the debounced value
     * as the tag label should be considered as identical as the tag without these spaces.
     */
    const trimmedDebouncedValue = React.useMemo(() => {
        return trim(debouncedTagName, ALL_SPACES_PATTERN);
    }, [debouncedTagName]);

    /** Fetch the suggestions */
    const { data: tagSuggestions = [], baseLoadingStatus } = useSuggestTagzQuery(
        {
            // Use the trimmed value to avoid fetching when user only adds a space
            query: trimmedDebouncedValue,
            params: {
                tagz: tags,
                limit: 3,
            },
        },
        {
            /**
             * Only fetch when both tagName and trimmedAndFormattedTagNameInput are set.
             * This makes sure no call is sent when the input field gets emptied.
             * */
            enabled: Boolean(tagName && trimmedDebouncedValue),
        },
    );

    /** Add the "debouncing" status as "loading" status */
    const isLoading = tagName !== debouncedTagName || baseLoadingStatus === BaseLoadingStatus.loading;

    /**
     * Track input ref to be able to set the focus on the input if needed
     */
    const inputRef = React.useRef<HTMLInputElement>(null);

    /**
     * Defined whether the current tagName is valid.
     * A new tag is defined as valid if:
     * * The tag name is actually valid on itself (via isTagNameValid utils)
     * * The tag name is not already suggested (via isTagDuplicated).
     */
    const hasValidNewTag = React.useMemo(() => {
        const normalizedTag = createTagSuggestion(debouncedTagName);

        return isTagNameValid(debouncedTagName) && !isTagDuplicated(tagSuggestions, normalizedTag);
    }, [debouncedTagName, tagSuggestions]);

    /**
     * The tagz label list in lowercase will be
     * use to exclude tagz already added from suggestions
     */
    const tagzLabelListInLowerCase = React.useMemo(() => {
        return Array.isArray(tags) ? tags.map((tag) => normalizeTagLabel(tag.label)) : [];
    }, [tags]);

    /**
     * Trigger a field focus when `shouldFocus` prop changes to `true`.
     */
    React.useEffect(() => {
        if (shouldFocus) {
            inputRef?.current?.focus();
        }
    }, [shouldFocus]);

    /**
     * Function triggered by the `onChange` event on the Text field. Here, we trigger the callback
     * passed by props and set the suggestions as visible depending on whether the query is valid.
     * @param {string} value New value entered on the text field.
     */
    const handleSearchChange = (value?: string) => {
        // Remove forbidden characters, smash spaces (regular and japanese)
        const formattedValue = formatTagNameInput(value);
        if (isTagzSearchValid(formattedValue)) {
            setTagName(formattedValue);
        }
    };

    /**
     * Function triggered when the user decides to create a new Tag.
     * @param {string} newTagName Tag name entered.
     */
    const handleCreateNewTag = (newTagName: string) => {
        const newTag = createTagSuggestion(newTagName);
        if (isTagNameValid(newTagName) && !isTagDuplicated(tags, newTag)) {
            onTagsChange([...tags, newTag]);
            setTagName('');
        }
    };

    /** Check if the tag has already been added or not. */
    const isTagAlreadyAdded = tagzLabelListInLowerCase.includes(normalizeTagLabel(trimmedDebouncedValue));

    /**
     * Only show the first option after loading is finished and if a new tag can be added
     */
    const showBeforeOption = !isLoading && hasValidNewTag && isNewTagAllowed;

    return (
        <SelectTextField
            status={tagName && isLoading ? BaseLoadingStatus.loading : BaseLoadingStatus.idle}
            hideToggle
            theme={theme}
            icon={icon || mdiPound}
            className={block({
                inline: isInline,
            })}
            inputRef={inputRef}
            label={isInline || shouldHideLabel ? undefined : translateKey(GLOBAL.TAGS)}
            aria-label={isInline || shouldHideLabel ? translateKey(GLOBAL.TAGS) : undefined}
            selectionType="multiple"
            onBlur={onBlur}
            onFocus={onFocus}
            value={tags}
            onChange={onTagsChange}
            options={tagName && !isLoading ? tagSuggestions : []}
            getOptionId={(suggestion) => suggestion.id}
            autoFilter={false}
            onSearch={handleSearchChange}
            searchInputValue={tagName}
            showEmptyState={false}
            beforeOptions={
                showBeforeOption ? (
                    <>
                        <SelectTextField.Option
                            id="add-entry"
                            onSelect={() => handleCreateNewTag(trimmedDebouncedValue)}
                            isDisabled={isTagAlreadyAdded}
                            after={isTagAlreadyAdded ? translateKey(GLOBAL.ALREADY_ADDED) : undefined}
                            autofocus
                        >
                            {isTagAlreadyAdded
                                ? trimmedDebouncedValue
                                : translateAndReplace(FOLKSONOMY.NEW_TAG, {
                                      TAG_NAME: trimmedDebouncedValue,
                                  })}
                        </SelectTextField.Option>

                        <SelectTextField.Divider />
                    </>
                ) : undefined
            }
            renderOption={(suggestion, index) => {
                const { label } = suggestion;
                return (
                    <SelectTextField.Option
                        // Focus the first option to easily add it while typing
                        autofocus={!showBeforeOption && index === 0}
                        before={<Icon icon={mdiPound} size={Size.xxs} />}
                        {...get({
                            element: 'suggest-item',
                            action: suggestion.id,
                            position: index,
                        })}
                    >
                        <LabelWithHighlightedQuery label={label} searchQuery={trimmedDebouncedValue} />
                    </SelectTextField.Option>
                );
            }}
            renderChip={(tag) => (
                <Chip onBlur={onTagBlur} onFocus={onTagFocus} before={<Icon icon={mdiPound} size={Size.xxs} />}>
                    {tag.label}
                </Chip>
            )}
            listProps={get({ element: 'dropdown', action: 'suggest' })}
            {...get({ element: 'autocomplete' })}
        />
    );
};

export { TagsAutocomplete };
